import { parseApprovalFristFromPayload } from './verfahrensmotor/parseApprovalFrist';
import type { ApprovalFristPayload } from './verfahrensmotor/fristApprovalTypes';
import { portalAuthzHeaders } from '../../constants';

export interface PortalDocumentFristProbeResult {
  ok: boolean;
  reply?: string;
  approvalFrist?: ApprovalFristPayload | null;
  skippedReason?: string;
}

/** Explizite Frist-Frage — darf auch nach abgeschlossener Auto-Analyse erneut laufen. */
export function isExplicitPortalFristQuestion(text: string): boolean {
  return /\b(frist(?:en)?|zustell(?:ung|t)?|berufungs?frist|berufung|revisions?frist|revision|rekursfrist|rekurs|rechtsmittel(?:e)?|verk[uü]nd(?:ung|et)?)\b/i.test(
    text.trim(),
  );
}

/** n8n-Antwort ohne belastbaren Fristtyp — keine Approval-Card im Chat. */
export function isFristProbeSkippedPayload(payload: Record<string, unknown>): boolean {
  const gate = typeof payload.qualityGate === 'string' ? payload.qualityGate : '';
  if (gate === 'frist_vorschlag_skipped' || gate === 'frist_motor_error') return true;
  if (payload.fristVorschlagActive === false) return true;
  const skipped = typeof payload.skippedReason === 'string' ? payload.skippedReason.trim() : '';
  return skipped.length > 0;
}

/**
 * Einmalige stille Frist-Probe nach Extract (n8n: extractDone + documentContext).
 * Wird nur vom Parent aufgerufen, wenn der Analyze-Guard noch nicht gesetzt ist.
 */
export async function probePortalDocumentFrist(args: {
  webhookUrl: string;
  timeoutMs: number;
  customerId: string;
  documentContext: string;
  extraBody?: Record<string, unknown>;
  quellDokumentNcPath?: string | null;
  quellDokumentPbId?: string | null;
  akteNcPath?: string | null;
  documentExtractFingerprint?: string | null;
  threadId?: string | null;
}): Promise<PortalDocumentFristProbeResult> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), args.timeoutMs);
  try {
    const response = await fetch(args.webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...portalAuthzHeaders(args.webhookUrl),
      },
      body: JSON.stringify({
        chatInput: '',
        history: [],
        mode: 'anwalts_arko',
        intent: 'document_extract_done',
        extractDone: true,
        customerId: args.customerId,
        documentContext: args.documentContext,
        ...(args.threadId ? { threadId: args.threadId } : {}),
        ...(args.documentExtractFingerprint
          ? { documentExtractFingerprint: args.documentExtractFingerprint }
          : {}),
        ...(args.quellDokumentNcPath
          ? { quell_dokument_nc_path: args.quellDokumentNcPath, quellDokumentNcPath: args.quellDokumentNcPath }
          : {}),
        ...(args.quellDokumentPbId
          ? { quell_dokument_pb_id: args.quellDokumentPbId, quellDokumentPbId: args.quellDokumentPbId }
          : {}),
        ...(args.akteNcPath ? { akte_nc_path: args.akteNcPath, akteNcPath: args.akteNcPath } : {}),
        ...(args.extraBody ?? {}),
      }),
      signal: controller.signal,
    });
    if (!response.ok) {
      return { ok: false, skippedReason: `http_${response.status}` };
    }
    const data = await response.json();
    const raw = data?.data ?? data;
    const item = Array.isArray(raw) ? raw[0] : raw;
    const payload = item?.json ?? item ?? data;
    const reply =
      payload?.output ??
      payload?.text ??
      payload?.message ??
      (typeof payload === 'string' ? payload : null);
    const skippedReason =
      typeof payload?.skippedReason === 'string' ? payload.skippedReason : undefined;

    if (payload && typeof payload === 'object') {
      const record = payload as Record<string, unknown>;
      if (isFristProbeSkippedPayload(record)) {
        return {
          ok: true,
          reply: typeof reply === 'string' ? reply : undefined,
          approvalFrist: null,
          skippedReason: skippedReason ?? 'skipped',
        };
      }
      const messageKind = typeof record.messageKind === 'string' ? record.messageKind : '';
      const gate = typeof record.qualityGate === 'string' ? record.qualityGate : '';
      const isApproval =
        messageKind === 'approval_frist' ||
        gate === 'frist_vorschlag_preview' ||
        record.fristVorschlagActive === true;
      const approvalFrist = isApproval ? parseApprovalFristFromPayload(payload) : null;
      return {
        ok: true,
        reply: typeof reply === 'string' ? reply : undefined,
        approvalFrist,
        skippedReason,
      };
    }

    return {
      ok: true,
      reply: typeof reply === 'string' ? reply : undefined,
      approvalFrist: null,
      skippedReason,
    };
  } catch {
    return { ok: false, skippedReason: 'network_or_timeout' };
  } finally {
    window.clearTimeout(timeout);
  }
}
